import type {
  ItemId,
  SuggestionsResponse,
} from 'api/definitions/intelligent-shopping-experience/types';
import { getDefaultUom } from 'utils/get-default-uom';
import type { Product } from 'constants/products';
import type {
  RecommendedSelectionWithKey,
  TransformedConflict,
  TransformedLegacyProduct,
} from 'redux/transforms/types';
import mapProductsIntoLegacyFormat from 'redux/transforms/transform-product-into-legacy-format';
import { weightOptions, WeightUnits } from 'constants/weightOptions';

type TransformedSuggestion = {
  itemId: ItemId;
  candidates: TransformedLegacyProduct[];
};

type TransformedSuggestionsResponse = {
  conflicts: TransformedConflict[];
  suggestions: TransformedSuggestion[];
  recommendedSelection: RecommendedSelectionWithKey[];
};

const findPreselectedCandidateIndex = (candidates: Product[]) =>
  candidates.findIndex(({ conflicts }) => conflicts.length === 0);

const hoistPreselectedCandidateToFront = ({
  candidates,
  preselectedIndex,
}: {
  candidates: TransformedLegacyProduct[];
  preselectedIndex: number;
}) => {
  if (preselectedIndex <= 0) {
    return candidates;
  }

  return [
    candidates[preselectedIndex],
    ...candidates.slice(0, preselectedIndex),
    ...candidates.slice(preselectedIndex + 1),
  ];
};

const createRecommendedSelectionItem = (
  itemId: ItemId,
  selectedCandidate: Product,
): RecommendedSelectionWithKey => {
  const { weights, lineNumber, id } = selectedCandidate;

  const uomId = getDefaultUom({
    defaultQuantity: weights.defaultQuantity,
    uoms: weights.uoms,
  });

  const amount = weights.defaultQuantity?.amount ?? weightOptions[uomId as WeightUnits].default;

  return {
    key: itemId,
    lineNumber,
    id,
    quantity: {
      amount,
      uomId,
    },
  };
};

const transformIntelligentShoppingExperienceSuggestionsIntoLegacyFormat = (
  response: SuggestionsResponse,
): TransformedSuggestionsResponse => {
  const conflicts: TransformedConflict[] = [];
  const suggestions: TransformedSuggestion[] = [];
  const recommendedSelection: RecommendedSelectionWithKey[] = [];

  for (const suggestion of response.suggestions) {
    const { conflicts: suggestionConflicts, products: transformedCandidates } =
      mapProductsIntoLegacyFormat({
        products: suggestion.candidates,
      });

    conflicts.push(...suggestionConflicts);

    const preselectedIndex = findPreselectedCandidateIndex(suggestion.candidates);

    const reorderedCandidates = hoistPreselectedCandidateToFront({
      candidates: transformedCandidates as TransformedLegacyProduct[],
      preselectedIndex,
    });

    suggestions.push({
      itemId: suggestion.itemId,
      candidates: reorderedCandidates,
    });

    if (preselectedIndex >= 0) {
      const preselectedCandidate = suggestion.candidates[preselectedIndex];
      const recommendedSelectionItem = createRecommendedSelectionItem(
        suggestion.itemId,
        preselectedCandidate,
      );

      recommendedSelection.push(recommendedSelectionItem);
    }
  }

  return {
    conflicts,
    suggestions,
    recommendedSelection,
  };
};

export default transformIntelligentShoppingExperienceSuggestionsIntoLegacyFormat;
